import React, { useContext } from 'react';
import Box from 'Components/Atoms/Box';
import ProductIcon from '../ProductIcon';
import { AuthContext, EXTERNAL_AUTH_TARGETS } from 'Contexts/AuthContext';
import './styles.scss';
import { Products } from 'utils/types';
import { dateHelper, noop } from 'utils/helper';
import useRestaurant from 'CustomHooks/useRestaurant';
import { AppId } from 'types/shared/documents/restaurants';
import usePinAuth, { SecurityPlusKeys } from 'CustomHooks/usePinAuth';

import firebase from 'utils/firebase';

export type ProductIconsProps = {
  products?: Products[];
  takeAwayNotifications?: number;
  restaurantId?: null | string;
};

const PRODUCTS: {
  id: string;
  product: string;
  label: string;
  linkTo?: (rId: string, apps: AppId[]) => string;
  authorize?: SecurityPlusKeys;
  app?: AppId;
  isNew?: boolean;
  hideLabel?: string;
  dontShowOnHide?: boolean;
  promotionalLink?: (rId: string) => string;
  onClick?: (rId: string | null) => void | Promise<void>;
}[] = [
  {
    id: 'reservations',
    product: 'reservation',
    label: 'Reservations',
    linkTo: (rId: string) => `${rId}/reservations/${dateHelper()}/tablePlan`,
  },
  {
    id: 'light-reservations',
    product: 'light-reservation',
    label: 'Reservations',
    linkTo: (rId: string) =>
      `/${rId}/reservations/${dateHelper()}/reservationList`,
  },

  {
    id: 'calendar',
    product: 'light-reservation, reservation',
    label: 'Calendar',
    linkTo: (rId: string) => `/${rId}/calendar/${dateHelper()}`,
    authorize: 'dashboard.calendar.read',
  },
  {
    id: 'guestbook',
    product: 'light-reservation, reservation',
    label: 'Guest Book',
    linkTo: (rId: string, apps) =>
      apps.includes('guestbook' as any)
        ? `/${rId}/guestbookV02`
        : `/${rId}/guestbook`,
    authorize: 'dashboard.guestBook.read',
  },
  {
    id: 'guest-statistics',
    product: 'general',
    label: 'Guest Statistics',
    linkTo: (rId: string) => `/${rId}/statistics`,
    authorize: 'dashboard.statistics.read',
  },
  {
    id: 'delivery',
    product: 'delivery',
    label: 'Take Away',
    linkTo: (rId: string) => `/${rId}/takeAway/orders/${dateHelper()}`,
    authorize: 'dashboard.delivery.read',
    dontShowOnHide: true,
  },
  {
    id: 'menu',
    product: 'menu',
    label: 'Digital Menu',
    app: 'digital_menu',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        if (!rId || !user) return;

        const idToken = await user.getIdToken(/* forceRefresh */ true);

        // Same "Login with Gastronaut" handoff as ?auth=menu-by-gastronaut,
        // plus the restaurant so the menu app opens its venue directly.
        window.location.href = `${EXTERNAL_AUTH_TARGETS['menu-by-gastronaut']}#token=${idToken}&restaurantId=${rId}`;
      } catch (error) {
        window.alert(error);
      }
    },
    authorize: 'dashboard.menu.read',
  },
  {
    id: 'feedback',
    product: 'feedback, followUp',
    label: 'Review Rocket',
    linkTo: (rId: string) => `${rId}/feedback`,
    authorize: 'dashboard.feedback.read',
  },
  {
    id: 'phone',
    product: 'general, phone',
    label: 'SIA',
    app: 'sia-phone-assistant',
    linkTo: (rId: string) => `/${rId}/sia-embed`,
    authorize: 'dashboard.sia.read',
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'addOns',
    product: 'reservation, addOns, addons',
    label: 'Add-Ons',
    linkTo: (rId: string) => `/${rId}/experiences`,
    hideLabel: 'COMING SOON',
    // isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'vouchers',
    product: 'vouchers',
    label: 'Vouchers',
    // hideLabel: "COMING SOON",
    linkTo: (rId: string) => `/${rId}/vouchers`,
    authorize: 'dashboard.voucher.read',
    promotionalLink: (rId: string) =>
      `/${rId}/settings/general/apps?appId=voucher`,
    // dontShowOnHide: true,
  },
  {
    id: 'newsletter',
    product: 'newsletters',
    label: 'Newsletter',
    // hideLabel: "COMING SOON",
    linkTo: (rId: string) => `/${rId}/newsletters`,
    dontShowOnHide: true,
  },
  {
    id: 'marketing',
    product: 'general',
    app: 'marketing' as any,
    label: 'Marketing',
    linkTo: (rId: string) => `/${rId}/marketing`,
    isNew: true,
    dontShowOnHide: true,
  },
  {
    id: 'tickets',
    product: 'reservation',
    app: 'gastronaut-events' as any,
    label: 'Events',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        const GASTRONAUT_EVENTS_URL = 'http://localhost:5173';

        if (user) {
          const idToken = await user.getIdToken(/* forceRefresh */ true);
          // Redirect to App B with the token
          window.location.href = `${GASTRONAUT_EVENTS_URL}/${
            rId || ''
          }?token=${idToken}`;
        } else {
          // Handle the case where the user is not logged in
        }
      } catch (error) {
        window.alert(error);
      }
    },
    dontShowOnHide: true,
  },
  {
    id: 'giftcards',
    product: 'vouchers',
    app: 'gastronaut-giftcards',
    label: 'Gift Cards',
    onClick: async (rId: string | null) => {
      try {
        const user = firebase.auth().currentUser;

        const GASTRONAUT_GIFTCARD_URL = 'http://localhost:5173';

        if (user) {
          const idToken = await user.getIdToken(/* forceRefresh */ true);
          // Redirect to App B with the token
          window.location.href = `${GASTRONAUT_GIFTCARD_URL}/${
            rId || ''
          }?token=${idToken}`;
        } else {
          // Handle the case where the user is not logged in
        }
      } catch (error) {
        window.alert(error);
      }
    },
    dontShowOnHide: true,
  },
  // {
  //   id: 'phone',
  //   product: 'general',
  //   app: 'sia-phone-assistant',
  //   label: 'Sia 2.0',
  //   onClick: async (rId: string | null) => {
  //     try {
  //       const user = firebase.auth().currentUser;

  //       const SIA_PHONE_ASSISTANT_URL = 'https://sia-app.gastronaut.ai';

  //       if (user) {
  //         const idToken = await user.getIdToken(/* forceRefresh */ true);
  //         // Redirect to App B with the token
  //         window.location.href = `${SIA_PHONE_ASSISTANT_URL}/login?token=${idToken}&restaurantId=${rId}`;
  //       } else {
  //         // Handle the case where the user is not logged in
  //       }
  //     } catch (error) {
  //       window.alert(error);
  //     }
  //   },
  //   isNew: true,
  //   dontShowOnHide: true,
  // },
  // {
  //   id: "order",
  //   product: "order",
  //   label: "Guest Order",
  //   linkTo: (rId: string) => `${rId}/order`,
  //   hideLabel: "COMING SOON",
  // },
];

const ProductIcons = ({
  products = [],
  takeAwayNotifications = 0,
  restaurantId = null,
}: ProductIconsProps) => {
  const { user } = useContext(AuthContext);
  const groups = user?.data?.groups ?? [];

  const { apps, hasSiaV02, siaState, setSupportOpen, setnotificationsV03Open } =
    useRestaurant();
  const hasSiaEmbed = apps.includes('sia-phone-assistant' as AppId);
  const siaBadgeCount = hasSiaV02
    ? (siaState.data?.calls_today ?? 0) + (siaState.data?.open_todos ?? 0)
    : 0;

  const { checkAuthentication } = usePinAuth();

  const filteredProducts = () => {
    return PRODUCTS.filter(
      (p) =>
        !(
          (products.includes('light-reservation') && p.id === 'reservations') ||
          (products.includes('reservation') && p.id === 'light-reservations')
        ),
    )
      .map((p) => ({
        ...p,
        locked: !!p.authorize && checkAuthentication(p.authorize),
        hide:
          !products.some((x) => p.product.includes(x)) ||
          !(!p.app || apps.includes(p.app as AppId)) ||
          (hasSiaEmbed && p.id === 'phone' && p.app === 'sia'),
      }))
      .sort((a, b) => (a.hide ? 1 : 0) - (b.hide ? 1 : 0))
      .filter((x) => !(x.hide && x.dontShowOnHide));
  };

  return (
    <Box className="product-icons">
      {filteredProducts().map((product, i) => (
        <ProductIcon
          key={product.id + '-' + i}
          {...product}
          notifications={product.id === 'phone' ? siaBadgeCount : undefined}
          restaurantId={restaurantId}
        />
      ))}
      <ProductIcon
        id="support"
        label="Support"
        onClick={() => {
          setnotificationsV03Open(false);
          setSupportOpen(true);
        }}
        restaurantId={restaurantId}
      />
      <ProductIcon
        id="faq"
        label="FAQ"
        linkTo={(rId) => ``}
        restaurantId={restaurantId}
        className="faq"
      />
      {groups.map((group) => (
        <ProductIcon
          key={group.id}
          id="group"
          label={group.name}
          // A group name is data. Typography would otherwise run it through
          // i18next, whose nsSeparator '_' swallows everything before the
          // first underscore.
          translation={null}
          linkTo={() => `/group/${group.id}`}
          restaurantId={restaurantId}
        />
      ))}
    </Box>
  );
};

export default ProductIcons;
